import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

type Tone = 'brand' | 'success' | 'warning' | 'danger' | 'neutral' | 'gold' | 'silver' | 'bronze' | 'info';

interface IconTileProps {
  icon: ReactNode;
  tone?: Tone;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Mjuk tonad bricka med rundade hörn — som ikonrutan uppe till höger i
 * nyckeltalskorten i referensbilderna. Ingen ram: tonen räcker, och en hårlinje
 * runt en 28-pixels ruta blir bara skräpig.
 */
const tones: Record<Tone, string> = {
  brand: 'bg-brand-50 text-brand-600',
  success: 'bg-emerald-50 text-emerald-600',
  warning: 'bg-amber-50 text-amber-600',
  danger: 'bg-red-50 text-red-600',
  neutral: 'bg-ink-100 text-ink-500',
  info: 'bg-indigo-50 text-indigo-600',
  gold: 'bg-amber-50 text-amber-600',
  silver: 'bg-ink-100 text-ink-500',
  bronze: 'bg-orange-50 text-orange-600',
};

const sizes = {
  sm: 'w-7 h-7 rounded-md',
  md: 'w-8 h-8 rounded-lg',
  lg: 'w-10 h-10 rounded-lg',
};

export function IconTile({ icon, tone = 'brand', size = 'md', className }: IconTileProps) {
  return (
    <span
      className={cn('inline-flex items-center justify-center shrink-0', tones[tone], sizes[size], className)}
      aria-hidden
    >
      {icon}
    </span>
  );
}
